import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Feather, Check, ArrowLeft, Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { billingPlans, billingMe, createCheckout } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useTranslation, translatePlanPrice, translatePlanFeature } from "@/lib/i18n";

export default function Pricing() {
    const nav = useNavigate();
    const { user } = useAuth();
    const { t, lang } = useTranslation();
    const [data, setData] = useState(null);
    const [me, setMe] = useState(null);
    const [busy, setBusy] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const [plans, meRes] = await Promise.all([billingPlans(), user ? billingMe() : Promise.resolve(null)]);
                setData(plans);
                setMe(meRes);
            } catch (e) {
                console.error("Pricing load failed", e);
                toast.error("Couldn't load pricing");
            }
        })();
    }, [user]);

    async function subscribe(packageId) {
        if (!user) { nav("/"); return; }
        setBusy(packageId);
        try {
            const res = await createCheckout(packageId, window.location.origin);
            window.location.href = res.url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't start checkout");
            setBusy("");
        }
    }

    const currentPlan = me?.plan || "thoughts";

    // Monthly package id per paid tier; the yearly one is the same key + "_yearly".
    const MONTHLY_PKG = {
        single_dream: "single_dream_monthly",
        dreamer_pro: "dreamer_pro_monthly",
        aza: "aza_monthly",
    };
    const YEARLY_PKG = {
        single_dream: "single_dream_yearly",
        dreamer_pro: "dreamer_pro_yearly",
        aza: "aza_yearly",
    };
    const CHOOSE_LABEL = {
        single_dream: "pricing.chooseEnchanter",
        dreamer_pro: "pricing.chooseMagnus",
        aza: "pricing.chooseAza",
    };

    const ctaForTier = (tierId) => {
        if (currentPlan === tierId) return { label: t("pricing.currentPlan"), disabled: true, pkg: null };
        if (tierId === "thoughts") return { label: t("pricing.default"), disabled: true, pkg: null };
        const pkg = MONTHLY_PKG[tierId];
        if (pkg) return { label: t(CHOOSE_LABEL[tierId]), disabled: false, pkg };
        return { label: "—", disabled: true, pkg: null };
    };

    return (
        <div className="min-h-screen bg-background text-foreground">
            <header className="px-6 lg:px-12 pt-8 pb-2 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2 hover:opacity-80" data-testid="back-to-app">
                    <Feather className="w-5 h-5 text-primary" />
                    <span className="font-heading text-2xl tracking-tight">Day Dreamer</span>
                </Link>
                <Button variant="ghost" size="sm" onClick={() => nav("/")} data-testid="pricing-back">
                    <ArrowLeft className="w-4 h-4" /> {t("common.back")}
                </Button>
            </header>

            <section className="px-6 lg:px-12 pt-10 pb-12 max-w-5xl mx-auto text-center">
                <div className="ui-label mb-4">{t("pricing.eyebrow")}</div>
                <h1 className="font-heading text-5xl sm:text-6xl font-light tracking-tight leading-[1.05]">
                    {t("pricing.titlePre")}<em className="text-primary">{t("pricing.titleEm")}</em>.
                </h1>
                <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
                    {t("pricing.subtitle")}
                </p>
            </section>

            <section className="px-6 lg:px-12 pb-12 max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {data?.tiers?.map((tier) => {
                    const cta = ctaForTier(tier.id);
                    const isPro = Boolean(tier.most_popular);
                    const yearlyPkg = YEARLY_PKG[tier.id];
                    return (
                        <Card
                            key={tier.id}
                            data-testid={`tier-card-${tier.id}`}
                            className={`p-7 flex flex-col ${isPro ? "border-primary/60 shadow-md relative" : ""}`}
                        >
                            {isPro && (
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-primary text-primary-foreground text-[10px] uppercase tracking-[0.2em]">
                                    {t("pricing.mostLoved")}
                                </div>
                            )}
                            <div className="ui-label">{t(tier.id === "thoughts" ? "pricing.freeTier" : "pricing.subscription")}</div>
                            <h3 className="font-heading text-3xl mt-2 font-normal">{tier.name}</h3>
                            <p className="text-sm text-muted-foreground mt-1">{translatePlanPrice(lang, tier.price_label)}</p>
                            {tier.price_note && (
                                <p className="text-xs text-primary/80 mt-1">{tier.price_note}</p>
                            )}
                            <ul className="mt-6 space-y-2.5 text-sm flex-1">
                                {tier.features.map((f) => (
                                    <li key={`${tier.id}-${f}`} className="flex items-start gap-2">
                                        <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                                        <span className="text-foreground/85">{translatePlanFeature(lang, f)}</span>
                                    </li>
                                ))}
                            </ul>
                            <Button
                                data-testid={`subscribe-${tier.id}`}
                                disabled={cta.disabled || busy === cta.pkg}
                                onClick={() => cta.pkg && subscribe(cta.pkg)}
                                className="mt-7 w-full rounded-full"
                                variant={isPro ? "default" : (tier.id === "thoughts" ? "outline" : "secondary")}
                            >
                                {busy === cta.pkg ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                                {cta.label}
                            </Button>
                            {yearlyPkg && (
                                <Button
                                    data-testid={`subscribe-${yearlyPkg}`}
                                    variant="ghost"
                                    size="sm"
                                    className="mt-2 w-full text-xs"
                                    disabled={currentPlan === tier.id || busy === yearlyPkg}
                                    onClick={() => subscribe(yearlyPkg)}
                                >
                                    {busy === yearlyPkg ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                    {t("pricing.yearly")}
                                </Button>
                            )}
                        </Card>
                    );
                })}
            </section>

            {data?.fine_print?.length > 0 && (
                <section className="px-6 lg:px-12 pb-16 max-w-3xl mx-auto">
                    <ul className="space-y-2 text-xs text-muted-foreground text-center">
                        {data.fine_print.map((line) => (
                            <li key={line}>{line}</li>
                        ))}
                    </ul>
                    <p className="text-center mt-6">
                        <Link to="/faq" className="text-xs text-primary hover:underline" data-testid="pricing-faq-link">
                            Questions about plans and AI words? Read the FAQ
                        </Link>
                    </p>
                </section>
            )}

            <footer className="text-center pb-12 text-xs text-muted-foreground">
                {t("pricing.footer")}
            </footer>
        </div>
    );
}
